import { ChangeEvent, useCallback, useContext, useEffect, useMemo, useRef, useState, MouseEvent } from "react";
import { PROJECT_SETTINGS_DEFAULT } from "../../common/constants/ProjectConst";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Context } from "../..";
import { getParameterByName } from "../../helpers/TaskLib/getParameterByName";
import { IFunctionPermissions } from "../../common/api_interfaces/role/IFunctionPermissions";
import { AppContext } from "../../App";
import { handleError } from "../../helpers/ErrorLib/handleError";
import FunctionSystem from "../../models/functionCode/functionsSystem";
import { HISTORY_PAGE_SIZE } from "../../constants/TaskConst";
import { IBreadCrumb } from "../../models/IBreadCrumb";
import { urlFiller } from "../../helpers/paramsAutoFiller";
import { hasPermission } from "../../helpers/hasPermisson";
import { ITagFromDisplay } from "../../common/api_interfaces/tag/ITagFromDisplay";
import { ITag } from "../../common/api_interfaces/tag/ITag";
import { IProjectHistoryDisplay } from "../../common/api_interfaces/history/IProjectHistoryDisplay";
import { IProjectMinimal } from "../../common/api_interfaces/project/ICardProjectProject";
import { ProjectSettings } from "../../common/api_interfaces/project/ProjectSettings";
import { ITaskChecklistGroup } from "../../common/api_interfaces/taskChecklist/ITaskChecklistGroup";
import { IMessageActions } from "../../common/api_interfaces/actions/IMessageActions";
import { ITaskGeneralInfo } from "../../common/api_interfaces/task/ITaskGeneralInfo";
import { IGetTaskRequest } from "../../common/api_interfaces/task/IGetTaskRequest";
import { ITaskHistoryFilter } from "../../common/api_interfaces/history/ITaskHistoryFilter";
import { IGetTaskHistoryDisplay } from "../../common/api_interfaces/history/IGetTaskHistoryDisplay";
import { IGetAllTaskPerson, IPerson } from "../../common/api_interfaces/task/IGetAllTaskPerson";
import { FunctionsAccessRequest } from "../../common/api_interfaces/role/FunctionsAccessRequest";
import { ISubTaskView } from "../../common/api_interfaces/task/ISubTaskView";
import { ISubTask } from "../../common/api_interfaces/task/ISubTask";
import { ITeamFilter } from "../../component/Project/Team/Team";
import { IGetTeamDisplay } from "../../common/api_interfaces/project/IGetTeamDisplay";
import { AxiosError, isAxiosError } from "axios";
import { ITeamMemberDisplay } from "../../common/api_interfaces/project/ITeamDisplay";
import { TaskAssignmentManager } from "../../helpers/TaskAssignmentManager/TaskAssignmentManager";

export const useFuncTask = (boardId: number | undefined, id: number | undefined) => {
    const [isLoading, setIsLoading] = useState(true);

    const { store } = useContext(Context);
    const {
        setBreadCrumb,
        findBoard,
        sideBarProjects,
        setNavPanelHighlight,
        archivedPage,
        archivedProjects,
        showToast,
        handleShowLoader,
        systemUserAccess
    } = useContext(AppContext);

    const [searchParams, setSearchParams] = useSearchParams();

    // Информация о задаче
    const [taskInformation, setTaskInformation] = useState<ITaskGeneralInfo>();
    const [allPerson, setAllPerson] = useState<IGetAllTaskPerson | undefined>(undefined);
    const [allowedProjects, setAllowedProjects] = useState<IProjectMinimal[]>([]);
    const [projectSettings, setProjectSettings] = useState<ProjectSettings>(PROJECT_SETTINGS_DEFAULT);
    const [defaultBoardId, setDefaultBoardId] = useState<number>();
    const [historySkip, setHistorySkip] = useState<number>(0);
    const [haveMoreRecords, setHaveMoreRecords] = useState<boolean>(true);
    const [taskHistory, setTaskHistory] = useState<IProjectHistoryDisplay[]>([]);
    const [checklistGroups, setCheklistGroups] = useState<ITaskChecklistGroup[]>([]);
    const [tab, setTab] = useState<number>();
    const [locationHash, setLocationHash] = useState<string | undefined>(window.location.hash);
    const [taskHistoryFilters, setTaskHistoryFilters] = useState<ITaskHistoryFilter>({ taskId: Number(id) });
    const [userAccess, setUserAccess] = useState<IFunctionPermissions[]>([]);
    const [viewHistory, setViewHistory] = useState(false);
    const [editTask, setEditTask] = useState(false);
    const [tags, setTags] = useState<ITag[]>([]);
    const [timeSelectVariant, setTimeSelectVariant] = useState<number>();
    const [customHours, setCustomHours] = useState<number | undefined>(undefined);
    const [customMinutes, setCustomMinutes] = useState<number | undefined>(undefined);
    const [team, setTeam] = useState<ITeamMemberDisplay[] | undefined>(undefined);
    
    // Подзадачи
    const [subtasksHead, setSubtasksHead] = useState<ISubTaskView>(); // Дерево
    const [subTasks, setSubTasks] = useState<ISubTask[]>([]);

    // Переменные для быстрых фильтров
    const [dateStart, setDateStart] = useState<Date | undefined>(undefined);
    const [dateEnd, setDateEnd] = useState<Date | undefined>(undefined);
    const [percentageCompletion, setPercentageCompletion] = useState<number | undefined>(undefined);
    const [requiredTime, setRequiredTime] = useState<number | undefined>(undefined);
    const [executor, setExecutor] = useState<IPerson | undefined | null>(undefined);
    const [group, setGroup] = useState<IPerson[] | undefined>(undefined);
    // -----

    const [loadHistory, setLoadHistory] = useState(true);

    const scrollComponentDiv = useRef(null);

    const navigate = useNavigate();

    const currentPageBoardId = useMemo(() => boardId !== undefined ? +boardId : defaultBoardId, [boardId, defaultBoardId]);
    const defaultTab: string | null = searchParams.get("tab");
    let checkList = getParameterByName(window.location, "checkList");

    const assignmentManager = useMemo(
        () => new TaskAssignmentManager(team, executor, group, allPerson),
        [team, executor, group, allPerson]
    );

    // #region Функции

    const forceLinkTerm = useMemo(() => taskInformation?.externalParams?.forceLinkTerm === true, [taskInformation]);
    const forceLinkName = useMemo(() => taskInformation?.externalParams?.forceLinkName === true, [taskInformation]);
    const forceLinkDescription = useMemo(() => taskInformation?.externalParams?.forceLinkDescription === true, [taskInformation]);
    const forceLinkExecutor = useMemo(() => taskInformation?.externalParams?.forceLinkExecutor === true, [taskInformation]);

    // Загрузка команды для выбора исполнителя и подписчиков
    const loadPersons = useCallback(
        async (filters: ITeamFilter) => {
            const newPageReq: IGetTeamDisplay = { filters };

            try {
                const res = await store.projectService.getProjectTeamDisplayData(newPageReq);
                setTeam(res.data.team || undefined);
            } catch (error) {
                if (error instanceof AxiosError) {
                    showToast(error.response?.data?.message || "Ошибка загрузки команды");
                }
            }
        },
        [store.projectService, showToast]
    );

    const loadTaskInfo = useCallback(async (): Promise<void | ITaskGeneralInfo> => {
        if (!id) return;

        try {
            const reqBody: IGetTaskRequest = {
                taskId: id,
                boardId: boardId ? Number(boardId) : undefined,
            };

            const response = await store.taskService.getTask(reqBody);

            if (!response.data) {
                return;
            }

            setPercentageCompletion(response.data.percentageCompletion ?? undefined);
            setRequiredTime(response.data.allottedTime ?? undefined);
            setDateStart(response.data.dateStart ? new Date(response.data.dateStart) : undefined);
            setDateEnd(response.data.dateEnd ? new Date(response.data.dateEnd) : undefined);
            // setResponsible(response.data.);
            // setGroup();
            setTaskInformation(response.data);
            if (response.data.projectId) {
                await loadPersons({
                    projectId: response.data.projectId,
                    name: undefined,
                    role: undefined,
                });
            }

            return response.data;
        } catch (error) {
            handleError(error, {
                defaultMessage: "Неизвестная ошибка при загрузке задачи",
                showToast: showToast,
            });
            navigate(`/projects`);
        }
    }, [id, boardId, store.taskService, showToast, navigate]);

    const getAllTaskPerson = useCallback(async (): Promise<void> => {
        if (!id) return;

        try {
            const response = await store.taskService.getAllTaskPerson(Number(id));

            setAllPerson(response.data);
            setExecutor(response.data?.executor ?? undefined);
            setGroup(response.data?.group ?? undefined);
        } catch (error) {
            handleError(error, {
                defaultMessage: "Неизвестная ошибка при загрузке пользователей",
                showToast: showToast,
            });
        }
    }, [id, store.taskService, showToast]);

    const loadChecklist = useCallback(
        async (curentAccess?: IFunctionPermissions[], tabFind?: boolean): Promise<void> => {
            if (!id) return;

            try {
                if (store.getAvailableModules().checkLists) {
                    if (
                        hasPermission(systemUserAccess, "ChecklistAction", "view") 
                        || hasPermission(curentAccess ? curentAccess : userAccess, "ChecklistAction", "view")) {
                        const res = await store.taskService.getTaskChecklist(Number(id));
                        const groups = res.data;
                        setCheklistGroups(groups);

                        // if (!tabFind) {
                        //     if (groups?.length > 1 || (groups?.[0]?.checklist && groups[0].checklist.length > 0)) {
                        //         setTab(1);
                        //     }
                        // }
                    }
                }
            } catch (error) {
                handleError(error, {
                    defaultMessage: "Неизвестная ошибка при загрузке чек листа",
                    showToast: showToast,
                });
            }
        },
        [id, searchParams, store, showToast, userAccess, systemUserAccess]
    );

    const loadSubTasks = useCallback(async (): Promise<void> => {
        if (!id || store.getBlockSubtasks()) return;

        try {
            const [subTasksTreeResp, subtasksCandidates, projectsResp] = await Promise.all([
                store.taskService.getSubTasksTree(Number(id)),
                store.taskService.getAvailableSubTasks(Number(id)),
                store.projectService.getAllowedProjects(Number(id)),
            ]);

            setSubtasksHead(subTasksTreeResp.data);
            setSubTasks(subtasksCandidates.data);
            setAllowedProjects(projectsResp.data);
        } catch (error) {
            handleError(error, {
                defaultMessage: "Неизвестная ошибка при загрузке подзадач",
                showToast: showToast,
            });
        }
    }, [id, store, showToast]);

    const loadTaskHistory = useCallback(
        async (
            skip: number,
            take: number,
            filters: ITaskHistoryFilter | undefined,
            append: boolean
        ): Promise<boolean> => {
            if (!id) return false;

            const req: IGetTaskHistoryDisplay = {
                skip,
                take,
                filters: filters ?? { taskId: Number(id) },
                sort: { date: "desc" },
            };

            if (!filters?.dateFrom || !filters?.dateTo || !filters?.userIds) {
                setLoadHistory(true);
            }

            try {
                const response = await store.taskService.getTaskHistory(req);
                const res = response.data;

                if (res.length < take) {
                    setLoadHistory(false);
                }

                if (!Array.isArray(res)) return false;

                setTaskHistory((prev) => (append ? [...prev, ...res] : res));
                return res.length > 0;
            } catch (error) {
                handleError(error, {
                    defaultMessage: "Неизвестная ошибка при загрузке истории",
                    showToast: showToast,
                });
                return false;
            }
        },
        [id, store.taskService, showToast]
    );

    const changeTab = useCallback((newTab: number) => {
        setTab(newTab);
        setLocationHash("");
    }, []);

    const onScroll = useCallback(
        async (e: React.UIEvent<HTMLDivElement>) => {
            const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

            if (scrollTop + clientHeight >= scrollHeight - 100 && tab === 3 && loadHistory) {
                const currentSkip = taskHistory.length;
                const newData = await loadTaskHistory(currentSkip, HISTORY_PAGE_SIZE, { taskId: Number(id) }, true);
            }
        },
        [tab, taskHistory.length, id]
    );

    // Универсальная функция обновления полей задачи
    const updateTaskField = useCallback(
        async (field: "priority" | "type", id: number) => {
            if (!editTask) {
                showToast("У вас нет прав для редактирования задачи!");
                return;
            }

            if (!taskInformation) return;

            try {
                handleShowLoader(true);
                const actionParams =
                    field === "priority"
                        ? { changePriorotyParam: { priorityId: id } }
                        : { changeTypeParam: { typeId: id } };

                const body: IMessageActions = {
                    tasks: [{ taskId: taskInformation.id }],
                    boardId: taskInformation.board.id,
                    actionParams,
                };

                await store.taskService.executeActions(body);
                await loadTaskInfo();
                showToast("Атрибуты задачи успешно обновлены");
            } catch (error) {
                handleError(error, {
                    defaultMessage: "Неизвестная ошибка при обновлении полей задачи",
                    showToast: showToast,
                });
            } finally {
                handleShowLoader(false);
            }
        },
        [editTask, taskInformation, store.taskService, loadTaskInfo, showToast]
    );

    // Обновление приоритета
    const updatePriority = useCallback(
        (id: number) => {
            updateTaskField("priority", id);
        },
        [updateTaskField]
    );

    // Обновление типа
    const updateType = useCallback(
        (id: number) => {
            updateTaskField("type", id);
        },
        [updateTaskField]
    );

    // Добавление тега
    const addTags = useCallback(
        async (_tags: ITag[]) => {
            if (!_tags) return;

            setTags(_tags.sort((a, b) => a.name.length - b.name.length));

            try {
                if (!taskInformation?.id || !taskInformation?.projectId) {
                    throw new Error("Информация о задаче отсутствует");
                }

                const body: ITagFromDisplay = {
                    projectId: taskInformation.projectId,
                    taskId: taskInformation.id,
                    tags: _tags,
                };

                await store.taskService.addTagsInTasks(body);
                await loadTaskInfo();

                if (taskInformation?.tags) {
                    setTags(taskInformation.tags.sort((a, b) => a.name.length - b.name.length));
                }
            } catch (error) {
                if (taskInformation?.tags) {
                    setTags(taskInformation.tags.sort((a, b) => a.name.length - b.name.length));
                }
                handleError(error, {
                    defaultMessage: "Неизвестная ошибка при добавлении тега(ов)",
                    showToast: showToast,
                });
            }
        },
        [taskInformation, store.taskService, loadTaskInfo, showToast]
    );

    // Удаление тега
    const removeTags = useCallback(
        async (_tags: ITag | ITag[]) => {
            if (!_tags) return;

            try {
                if (!taskInformation?.id || !taskInformation?.projectId) {
                    throw new Error("Информация о задаче отсутствует");
                }

                const tagsToRemove = Array.isArray(_tags)
                    ? tags.filter((xx) => _tags.some((tag) => tag.id === xx.id))
                    : tags.filter((xx) => _tags.id === xx.id);

                if (tagsToRemove.length === 0) return;

                const updatedTags = tags.filter((xx) =>
                    Array.isArray(_tags) ? !_tags.some((tag) => tag.id === xx.id) : _tags.id !== xx.id
                );
                setTags(updatedTags.sort((a, b) => a.name.length - b.name.length));

                const body: ITagFromDisplay = {
                    projectId: taskInformation.projectId,
                    taskId: taskInformation.id,
                    tags: tagsToRemove,
                };

                await store.taskService
                    .removeTagsInTasks(body)
                    .then((xx) => loadTaskInfo())
                    .catch((error) => {
                        throw new Error(`Произошла ошибка при удалении тега: ${error}`);
                    });
            } catch (error) {
                handleError(error, {
                    defaultMessage: "Неизвестная ошибка при удалении тега(ов)",
                    showToast: showToast,
                });
            }
        },
        [tags, taskInformation, store.taskService, loadTaskInfo, showToast]
    );

    const loadProjectSettings = useCallback(async (taskData: ITaskGeneralInfo): Promise<void> => {
        try {
            if (!taskData) {
                return;
            }

            const settingsResponse = await store.projectService.getProjectSettings(taskData.projectId);

            if (!settingsResponse) {
                return;
            }

            setProjectSettings(settingsResponse.data);

            setDefaultBoardId(taskData?.board.id);

            if (taskData?.tags) {
                setTags(taskData.tags.sort((a, b) => a.name.length - b.name.length));
            }
        } catch (error) {
            console.error(error);
            throw error;
        }
    }, []);

    const loadAccessUser = useCallback(
        async (curProjectId: number, curBoardId: number): Promise<IFunctionPermissions[]> => {
            try {
                const functionClass = new FunctionSystem();

                const newCheckRole: FunctionsAccessRequest = {
                    projectId: curProjectId,
                    boardId: curBoardId,
                    functionCodes: functionClass.getTaskFunction,
                };

                const checkThisRole = await store.hasPermission(newCheckRole);
                setUserAccess(checkThisRole);

                const hasViewHistory = hasPermission(checkThisRole, "TaskAction", "view");
                const hasEditTask = hasPermission(checkThisRole, "TaskAction", "edit");

                setViewHistory(hasViewHistory);
                setEditTask(hasEditTask);

                return checkThisRole;
            } catch (error) {
                console.error(error);
                throw error;
            }
        },
        []
    );

    // Смена кастомного времени
    const onChangeCustomTime = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            setTimeSelectVariant(undefined);

            const { id, value } = event.target;
            const numericValue = value.replace(/\D/g, "");

            switch (id) {
                case "custom-hours-input":
                    setCustomHours(Number(numericValue.slice(0, 4)));
                    break;
                case "custom-minutes-input":
                    setCustomMinutes(Math.min(59, Number(numericValue.slice(0, 2))));
                    break;
            }
        },
        [setTimeSelectVariant, setCustomHours, setCustomMinutes]
    );

    // Установка выбранного времени
    const selectTimeVariant = useCallback(
        (event: MouseEvent<HTMLButtonElement>) => {
            const timeText = Number(event.currentTarget.textContent);

            setTimeSelectVariant(timeText);
            setCustomHours(timeText);
            setCustomMinutes(0);
        },
        [setTimeSelectVariant, setCustomHours, setCustomMinutes]
    );

    // #endregion

    // #region Функции быстрых фильтров
    const normalizeDate = useCallback((date: Date | null | undefined): Date | undefined => {
        return date ? new Date(date.toUTCString()) : undefined;
    }, []);

    // Общие функции обновления быстрых фильтров

    useEffect(() => {
        if (allPerson?.group) {
            setGroup(allPerson?.group);
        }
        if (allPerson?.executor) {
            setExecutor(allPerson?.executor);
        }
    }, [allPerson]);

    const fastTaskUpdate = useCallback(
        async (
            percentageCompletion?: number,
            requiredTime?: number,
            dateStart?: Date,
            dateEnd?: Date,
            responsible?: number | null,
            group?: number[]
        ) => {
            try {
                if (id === undefined) {
                    throw new Error("Отстутсвует информация о задаче");
                }

                // Возможно подписчики будут пропадать, проверить

                await store.taskService.editTask({
                    id: id,
                    percentageCompletion: percentageCompletion,
                    requiredTime: requiredTime,
                    startDate: normalizeDate(dateStart),
                    endDate: normalizeDate(dateEnd),
                    responsible: responsible,
                    group: group,
                });

                await loadTaskInfo();
                await getAllTaskPerson();
                showToast("Данные сохранены");
            } catch (err) {
                if (isAxiosError(err)) {
                    showToast(err.response?.data.message);
                } else {
                    showToast("Ошибка обновления задачи");
                }
            }
        },
        [id, loadTaskInfo, normalizeDate, showToast, store.taskService]
    );

    const updatePercentageCompletion = useCallback(
        async (percentageCompletion: number) => {
            try {
                setPercentageCompletion(percentageCompletion);
                await fastTaskUpdate(percentageCompletion);
            } catch (error) {
                showToast("Ошибка обновления процента выполнения");
            }
        },
        [fastTaskUpdate]
    );

    const updateRequiredTime = useCallback(async (requiredTime: number) => {
        try {
            setRequiredTime(requiredTime);
            await fastTaskUpdate(undefined, requiredTime);
        } catch (error) {
            showToast("Ошибка обновления плановых трудозатрат");
        }
    }, []);

    const updateDate = useCallback(
        async (typeDate: "start" | "end", date: Date) => {
            const typeDateUpdate = typeDate === "start";

            try {
                await fastTaskUpdate(
                    undefined,
                    undefined,
                    typeDate === "start" ? date : undefined,
                    typeDate === "end" ? date : undefined
                );
            } catch (error) {
                showToast(`Ошибка обновления даты ${typeDateUpdate ? "начала" : "окончания"}`);
            }
        },
        [fastTaskUpdate, showToast]
    );

    // Мемоизация данных для избежания лишних вычислений
    const { visibleGroup, hiddenGroup, hasMore } = useMemo(
        () => ({
            visibleGroup: group?.slice(0, 4) || [],
            hiddenGroup: group?.slice(4) || [],
            hasMore: (group?.length || 0) > 4,
        }),
        [group]
    );

    const filteredChoiseExecutor = useMemo<
        {
            id: number;
            role: string;
            name: string;
            surname: string;
            middleName: string;
            photoId: number | undefined;
        }[]
    >(() => {
        return assignmentManager.getFilteredChoiceExecutor();
    }, [team, executor]);

    const filteredChoiceGroup = useMemo<
        {
            id: number;
            role: string;
            name: string;
            surname: string;
            middleName: string;
            photoId: number | undefined;
        }[]
    >(() => {
        return assignmentManager.getFilteredChoiceGroup();
    }, [group, filteredChoiseExecutor]);

    // Очистка исполнителя
    // const clearResponsible = useCallback(() => {
    //     setExecutor(undefined);
    // }, []);

    // Изменение исполнителя
    const handleSelectExecutor = useCallback(
        async (userId: number | null) => {
            try {
                const result = assignmentManager.handleSelectExecutor(userId);
                setExecutor(result.executor);
                setGroup(result.group);

                await fastTaskUpdate(
                    undefined,
                    undefined,
                    undefined,
                    undefined,
                    result.executor === null ? null : result.executor?.id,
                    result.group?.map((xx) => xx.id)
                );
            } catch (error) {
                showToast("Произошла ошибка смены исполнителя");
            }
        },
        [allPerson, group, setGroup, team, executor]
    );

    // Добавление в группу
    const handleSelectGroup = useCallback(
        async (userId: number) => {
            const result = assignmentManager.handleSelectGroup(userId);
            setExecutor(result.executor);
            setGroup(result.group);

            try {
                await fastTaskUpdate(
                    undefined,
                    undefined,
                    undefined,
                    undefined,
                    result.executor ? result.executor.id : null,
                    result.group?.map((xx) => xx.id)
                );
            } catch (error) {
                showToast("Произошла ошибка смены исполнителя");
            }
        },
        [
            // clearResponsible,
            setGroup,
            group,
            executor,
            team,
        ]
    );

    // Удаление из группы
    const handleRemoveGroup = useCallback(
        async (userId: number) => {
            const result = assignmentManager.handleRemoveGroup(userId);
            setGroup(result.group);

            try {
                await fastTaskUpdate(
                    undefined,
                    undefined,
                    undefined,
                    undefined,
                    undefined,
                    result.group?.map((xx) => xx.id)
                );
            } catch (error) {
                showToast("Произошла ошибка смены исполнителя");
            }
        },
        [group, setGroup]
    );

    // #endregion

    // #region Эффекты

    const loadHock = useCallback(async () => {
        handleShowLoader(true);
        try {
            if (!id)
                return;
            const tabParam = searchParams.get("tab");

            urlFiller(
                window.location.href,
                {
                    arguments: [
                        {
                            argument: "tab",
                            value: tabParam?.length ? tabParam : "0",
                        },
                    ],
                    fragment: [],
                },
                false,
                navigate,
                false
            );
            setTab(tabParam?.length ? Number(tabParam) : 0);

            let taskData = await loadTaskInfo();

            if (!taskData) {
                return;
            }

            let taskUserAccess = await loadAccessUser(taskData.projectId, taskData.board.id);

            await Promise.all([
                loadProjectSettings(taskData),
                loadChecklist(taskUserAccess, tabParam?.length ? true : false),
                getAllTaskPerson(),
                loadSubTasks(),
                loadTaskHistory(0, HISTORY_PAGE_SIZE, { taskId: id }, false),
            ]);
        } catch (error) {
            console.warn("Ошибка загрузки хука");
        } finally {
            handleShowLoader(false);
        }
    }, [id]);

    useEffect(() => {
        loadHock();
    }, [id]);

    // Эффект для обновления breadcrumb
    useEffect(() => {
        if (!taskInformation || !(boardId || defaultBoardId)) return;

        const projectsSource = archivedPage ? archivedProjects : sideBarProjects;
        const boardIdToUse = boardId ? Number(boardId) : defaultBoardId!;

        const bread: IBreadCrumb[] = [
            {
                label: archivedPage ? "Архив" : "Проекты",
                url: archivedPage ? "/archive" : "/projects",
            },
            ...findBoard(projectsSource, boardIdToUse, archivedPage),
            {
                label: taskInformation.shortDescription,
                url: `/task/${taskInformation.id}`,
            },
        ];

        setBreadCrumb(bread);
        if (!archivedPage) setNavPanelHighlight(undefined);
    }, [boardId, taskInformation, defaultBoardId, sideBarProjects, archivedPage, setBreadCrumb, setNavPanelHighlight]);

    // Эффект для обновления параметров URL
    useEffect(() => {
        if (tab !== undefined && !isNaN(tab)) {
            urlFiller(
                window.location.href,
                {
                    arguments: [
                        {
                            argument: "tab",
                            value: String(tab),
                        },
                    ],
                    fragment: [],
                },
                false,
                navigate
            );
        }
    }, [tab]);

    // Эффект для обновления тегов
    useEffect(() => {
        if (taskInformation?.tags) {
            setTags(taskInformation.tags.sort((a, b) => a.name.length - b.name.length));
        }
    }, [taskInformation]);
    // #endregion

    return {
        team,

        //Состояния загрузки
        isLoading,
        setIsLoading,

        //Основные данные задачи
        taskInformation,
        setTaskInformation,
        allPerson,
        setAllPerson,
        projectSettings,
        setProjectSettings,
        defaultBoardId,
        setDefaultBoardId,
        timeSelectVariant,
        setTimeSelectVariant,

        //Подзадачи
        subtasksHead,
        setSubtasksHead,
        subTasks,
        setSubTasks,
        allowedProjects,
        setAllowedProjects,

        //Чеклисты
        checklistGroups,
        setCheklistGroups,

        //История задачи
        taskHistory,
        setTaskHistory,
        taskHistoryFilters,
        setTaskHistoryFilters,
        historySkip,
        setHistorySkip,
        haveMoreRecords,
        setHaveMoreRecords,
        viewHistory,
        setViewHistory,

        //UI состояния
        tab,
        setTab,
        locationHash,
        setLocationHash,
        editTask,
        setEditTask,
        forceLinkName,
        forceLinkDescription,
        forceLinkTerm,
        forceLinkExecutor,

        //Права доступа
        userAccess,
        setUserAccess,

        //Референсы
        scrollComponentDiv,

        //Функции загрузки данных
        loadTaskInfo,
        getAllTaskPerson,
        loadChecklist,
        loadSubTasks,
        loadTaskHistory,

        //Функции обновления данных
        updatePriority,
        updateType,
        addTags,
        removeTags,
        selectTimeVariant,
        onChangeCustomTime,

        fastTaskUpdate,

        //Функции UI взаимодействия
        changeTab,
        onScroll,

        //Вспомогательные значения
        currentPageBoardId,
        defaultTab,
        checkList,

        // Быстрое обновление
        setPercentageCompletion,
        setRequiredTime,
        setDateStart,
        setDateEnd,
        setExecutor,
        setGroup,

        dateStart,
        dateEnd,
        percentageCompletion,
        requiredTime,
        executor,
        group,
        visibleGroup,
        hiddenGroup,
        hasMore,
        filteredChoiseExecutor,
        filteredChoiceGroup,

        updatePercentageCompletion,
        updateRequiredTime,
        updateDate,
        handleSelectExecutor,
        handleSelectGroup,
        handleRemoveGroup,
    };
};
